import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  FormGroup,
  Grid,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import IconButtons from "../../../components/IconButtons/IconButtons";
import { cross_default_icon } from "../../../constants/icons";
import { font_15, isOnlyTrueForPermissionKey } from "../../../utils/utils";
import AutoCompleteTagInput from "./../../../components/AutoCompleteTagInput/AutoCompleteTagInput";
import Loader from "../../../components/Loader/Loader";
import SimpleAutoComplete from "../../../components/SimpleAutoComplete/SimpleAutoComplete";
import { useSelector } from "react-redux";

const FilterAttendeesTab = ({
  open,
  onClose,
  handleFilterAttendeeSubmit,
  handleFilterAttendeeRemove,
  handleChangeFilterAttendee,
  filterAttendee,
  competency,
  bootcamp,
  batches,
  apiLoading,
  isMentor,
  isSpoc,
  isHr,
  isPresenter,
  isReviewer,
  permissions,
}) => {
  const statusOptions = useSelector(
    (state) => state?.utility?.constants?.sessionAttendanceStatuses
  );
  const isOnlySessionPresenter = isOnlyTrueForPermissionKey(
    permissions,
    "sessionPresenter"
  );
  const isOnlyMentor = isOnlyTrueForPermissionKey(permissions, "mentor");
  const isOnlyReviewer = isOnlyTrueForPermissionKey(
    permissions,
    "exerciseReviewer"
  );

  // Check if any of the three permissions are true
  const hasAnyPermission = isPresenter || isMentor || isReviewer;

  return (
    <>
      {apiLoading && <Loader />}
      <Drawer
        className="filter-drawer-container"
        anchor="right"
        open={open}
        onClose={onClose}
      >
        <Grid container className="filter-main-container">
          <Grid item md={12} pt={2} className="drawer-input-container">
            <Box className="filter-head">
              <Typography variant="font_20" className="filter-title ">
                Filters
              </Typography>
              <IconButtons
                  handleClick={onClose}
                  height={font_15}
                  width={font_15}
                  image={cross_default_icon}
                  tooltip="Close Filter"
              />
            </Box>
            <Box className="filter-padding textfield-box">
              <TextField
                size="small"
                label="Attendee Name/Email"
                color="secondary"
                fullWidth
                className="filter-inputs"
                margin="dense"
                variant="outlined"
                value={filterAttendee?.attendeeNameOrEmail}
                onChange={(e) =>
                  handleChangeFilterAttendee(
                    "attendeeNameOrEmail",
                    e?.target?.value
                  )
                }
              />

              {(isSpoc || (isSpoc && hasAnyPermission) || isHr) && (
                <TextField
                  size="small"
                  label="Mentor Name/Email"
                  color="secondary"
                  fullWidth
                  className="filter-inputs"
                  margin="dense"
                  variant="outlined"
                  value={filterAttendee?.mentorNameOrEmail}
                  onChange={(e) =>
                    handleChangeFilterAttendee(
                      "mentorNameOrEmail",
                      e?.target?.value
                    )
                  }
                />
              )}
              <SimpleAutoComplete
                label="Status"
                placeholder="Select Status"
                options={statusOptions}
                onChange={(event, newValue) =>
                  handleChangeFilterAttendee("status", newValue)
                }
                getOptionLabel={(option) => option?.value}
                value={filterAttendee?.status}
                renderOption={(props, option, value) => {
                  return (
                    <li
                      {...props}
                      key={props?.id}
                      className={
                        option?.value === filterAttendee?.status?.value
                          ? "multi-tag-edit-list-selected"
                          : "multi-tag-style"
                      }
                    >
                      <Box lineHeight={1.1}>
                        <Box>{`${option?.value}`}</Box>
                      </Box>
                    </li>
                  );
                }}
              />
              {isHr && (
                <AutoCompleteTagInput
                  label="Competency"
                  placeholder={
                    filterAttendee?.competencyIds?.length
                      ? null
                      : "Select Competency"
                  }
                  options={competency}
                  selectedValue={filterAttendee?.competencyIds}
                  getOptionLabel={(option) => option?.name}
                  filterOptions={(options, params) => {
                    // <<<--- inject the Select All option
                    const filter = createFilterOptions();
                    const filtered = filter(options, params);
                    if (competency.length === 0) {
                      return [{ name: "No options", all: false }];
                    }
                    return [{ name: "Select All", all: true }, ...filtered];
                  }}
                  onChange={(event, newValue) => {
                    const idCounts = newValue.reduce((counts, { id }) => {
                      counts[id] = (counts[id] || 0) + 1;
                      return counts;
                    }, {});
                    const uniqueNewValue = newValue.filter(({ id }) => {
                      return idCounts[id] === 1;
                    });
                    if (newValue.find((option) => option.all)) {
                      return handleChangeFilterAttendee(
                        "competencyIds",
                        filterAttendee?.competencyIds?.length ===
                          competency?.length
                          ? []
                          : competency
                      );
                    }
                    handleChangeFilterAttendee("competencyIds", uniqueNewValue);
                  }}
                  renderOption={(props, option, value) => {
                    // Check if option is "No options" and render accordingly
                    if (option.name === "No options") {
                      return (
                        <Box className="auto-complete-no-options">
                          <Box>{option?.name}</Box>
                        </Box>
                      );
                    }
                    const optionChecked =
                      (option.all &&
                        filterAttendee?.competencyIds?.length ===
                          competency?.length) ||
                      filterAttendee?.competencyIds?.find(
                        (e) => e?.id === option?.id
                      ) != null;
                    return (
                      <li
                        {...props}
                        key={props?.id}
                        className={
                          optionChecked
                            ? "multi-tag-edit-list-selected"
                            : "multi-tag-style"
                        }
                      >
                        <Box className="multi-tag-input-list-padding">
                          <Checkbox
                            className="auto-complete-checkbox-list"
                            size="small"
                            checked={optionChecked}
                            color="secondary"
                          />
                          {option?.name}
                        </Box>
                      </li>
                    );
                  }}
                />
              )}
              {(hasAnyPermission || isSpoc || isHr) && (
                <SimpleAutoComplete
                  disabled={!isHr}
                  label="Bootcamp"
                  placeholder="Select Bootcamp"
                  options={bootcamp}
                  onChange={(event, newValue) =>
                    handleChangeFilterAttendee("bootcampIds", newValue)
                  }
                  getOptionLabel={(option) => option?.name}
                  value={filterAttendee?.bootcampIds}
                  renderOption={(props, option, value) => {
                    return (
                      <li
                        {...props}
                        key={props?.id}
                        className={
                          option?.id === filterAttendee?.bootcampIds?.id
                            ? "multi-tag-edit-list-selected"
                            : "multi-tag-style"
                        }
                      >
                        <Box lineHeight={1.1}>
                          <Box>{`${option?.name}`}</Box>
                        </Box>
                      </li>
                    );
                  }}
                  tooltipTitle={filterAttendee?.bootcampIds?.name}
                />
              )}
              {(hasAnyPermission || isSpoc || isHr) && (
                <AutoCompleteTagInput
                  label="Batches"
                  placeholder={
                    filterAttendee?.batchIds?.length ? null : "Select Batch"
                  }
                  options={batches}
                  selectedValue={filterAttendee?.batchIds}
                  getOptionLabel={(option) => `${option?.name}`}
                  filterOptions={(options, params) => {
                    // <<<--- inject the Select All option
                    const filter = createFilterOptions();
                    const filtered = filter(options, params);
                    if (batches.length === 0) {
                      return [{ name: "No options", all: false }];
                    }
                    return [{ name: "Select All", all: true }, ...filtered];
                  }}
                  onChange={(event, newValue) => {
                    const idCounts = newValue.reduce((counts, { id }) => {
                      counts[id] = (counts[id] || 0) + 1;
                      return counts;
                    }, {});
                    const uniqueNewValue = newValue.filter(({ id }) => {
                      return idCounts[id] === 1;
                    });
                    if (newValue.find((option) => option.all)) {
                      return handleChangeFilterAttendee(
                        "batchIds",
                        filterAttendee?.batchIds?.length === batches?.length
                          ? []
                          : batches
                      );
                    }
                    handleChangeFilterAttendee("batchIds", uniqueNewValue);
                  }}
                  renderOption={(props, option, value) => {
                    // Check if option is "No options" and render accordingly
                    if (option.name === "No options") {
                      return (
                        <Box className="auto-complete-no-options">
                          <Box>{option?.name}</Box>
                        </Box>
                      );
                    }
                    const optionChecked =
                      (option.all &&
                        filterAttendee?.batchIds?.length === batches?.length) ||
                      filterAttendee?.batchIds?.find(
                        (e) => e?.id === option?.id
                      ) != null;
                    return (
                      <li
                        {...props}
                        key={props?.id}
                        className={
                          optionChecked
                            ? "multi-tag-edit-list-selected"
                            : "multi-tag-style"
                        }
                      >
                        <Box className="multi-tag-input-list-padding">
                          <Checkbox
                            className="auto-complete-checkbox-list"
                            size="small"
                            checked={optionChecked}
                            color="secondary"
                          />
                          {`${option?.name}`}
                        </Box>
                      </li>
                    );
                  }}
                />
              )}
              {!(isSpoc || (isSpoc && hasAnyPermission) || isHr) && (
                <Box>
                  <FormGroup>
                    {isPresenter && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="secondary"
                            size="small"
                            checked={filterAttendee?.showAttendees}
                            onChange={(e) =>
                              handleChangeFilterAttendee(
                                "showAttendees",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="Show my attendees"
                        disabled={isOnlySessionPresenter}
                      />
                    )}
                    {isMentor && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="secondary"
                            size="small"
                            checked={filterAttendee?.showMentees}
                            onChange={(e) =>
                              handleChangeFilterAttendee(
                                "showMentees",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="Show my mentees"
                        disabled={isOnlyMentor}
                      />
                    )}

                    {permissions?.exerciseReviewer && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="secondary"
                            size="small"
                            checked={filterAttendee?.showReviewees}
                            onChange={(e) =>
                              handleChangeFilterAttendee(
                                "showReviewees",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="Show my reviewees"
                        disabled={isOnlyReviewer}
                      />
                    )}
                  </FormGroup>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item md={12} className="filter-padding filter-btn-container">
            <Button
              onClick={handleFilterAttendeeSubmit}
              variant="contained"
              color="secondary"
              className="filter-btn margin-right-1"
            >
              <Typography variant="font_12_bold_700">Apply Filters</Typography>
            </Button>

            <Button
              onClick={handleFilterAttendeeRemove}
              variant="outlined"
              color="secondary"
              className="filter-btn"
            >
              <Typography variant="font_12_bold_700">Reset</Typography>
            </Button>
          </Grid>
        </Grid>
      </Drawer>
    </>
  );
};

export default FilterAttendeesTab;
